/**
 * pdf.js によるソースプロバイダ（FR-SRC-01〜03,05）。
 * pdf.js への依存はこのディレクトリの中だけに閉じる（coding.md §Client module rules）。
 */
import { GlobalWorkerOptions, PermissionFlag, getDocument } from 'pdfjs-dist';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { assetUrl } from '../../core/asset-base';
import { ViewerError } from '../../core/errors';
import type { ManifestSession } from '../../core/manifest';
import { FULL_PAGE } from '../../core/source';
import type { PageRaster, PageRasterRequest, SourceProvider } from '../../core/source';
import { maxWidthFor } from '../canvas-limits';
import { DEFAULT_CHUNK, SignedRangeTransport } from './range-transport';

/** ベクタなので原理的な上限は無いが、暴走を防ぐための安全弁を置く。 */
const MAX_EFFECTIVE_WIDTH = 20000;

/** pdf.js の worker は 1 度だけ設定する（同一バージョンのコアと必ず対で使う）。 */
function configureWorker(assetBase: string): void {
  if (GlobalWorkerOptions.workerSrc) return;
  GlobalWorkerOptions.workerSrc = assetUrl('pdf.worker.min.mjs', assetBase);
}

export interface PdfSourceOptions {
  /** 側面資産の基準 URL。空ならスクリプト自身の位置から解決する。 */
  assetBase: string;
  /** 取得失敗の通知先（描画中でない Range 失敗などを拾う）。 */
  onError: (error: unknown) => void;
  /** 読み込み進捗の通知先（起動表示のため。UI には依存しない＝ただのコールバック）。 */
  onProgress?: (loaded: number, total: number) => void;
  /** Range 取得のチャンク長（バイト・FR-SRC-02 の「チャンクサイズ設定可」）。 */
  rangeChunkSize?: number;
}

export class PdfSourceProvider implements SourceProvider {
  private loadingTask: PDFDocumentLoadingTask | null = null;
  private document: PDFDocumentProxy | null = null;
  private transport: SignedRangeTransport | null = null;
  private session: ManifestSession | null = null;
  private readonly pages = new Map<number, Promise<PDFPageProxy>>();

  constructor(private readonly options: PdfSourceOptions) {}

  async open(session: ManifestSession): Promise<void> {
    this.session = session;
    configureWorker(this.options.assetBase);

    const chunkSize = this.options.rangeChunkSize ?? DEFAULT_CHUNK;
    const opened = await SignedRangeTransport.open(
      session,
      this.options.onError,
      this.options.onProgress,
      chunkSize,
    );
    // Range が使えない配信経路では全体取得へ落ちる（FR-SRC-02）。転送層は作られない。
    this.transport = opened.kind === 'range' ? opened.transport : null;
    // pdf.js 6 は eval / new Function を使わないため、CSP に unsafe-eval は不要（NFR-SEC-01）。
    this.loadingTask = getDocument({
      ...(opened.kind === 'range'
        ? { range: opened.transport, rangeChunkSize: chunkSize }
        : { data: opened.data }),
      cMapUrl: assetUrl('cmaps/', this.options.assetBase),
      cMapPacked: true,
      standardFontDataUrl: assetUrl('standard_fonts/', this.options.assetBase),
      wasmUrl: assetUrl('wasm/', this.options.assetBase),
      iccUrl: assetUrl('iccs/', this.options.assetBase),
    });
    this.document = await this.loadingTask.promise;

    const expected = session.current.pageCount;
    if (this.document.numPages !== expected) {
      throw new ViewerError(
        'E_MANIFEST_MISMATCH',
        `マニフェストのページ数（${expected}）と PDF の実ページ数（${this.document.numPages}）が一致しない`,
      );
    }
  }

  get pageCount(): number {
    return this.document?.numPages ?? 0;
  }

  /**
   * 原本 PDF 自身が許している操作（FR-DL-02）。
   *
   * 暗号化辞書を持たない PDF では pdf.js が null を返す＝制限なしなので、両方を許可として扱う。
   * ダウンロードに対応する専用のフラグは PDF 仕様に無いため、**内容の抽出（COPY）**を可否の
   * 根拠にする（原本をそのまま渡すことは抽出の上位互換のため）。
   */
  async permissions(): Promise<{ download: boolean; print: boolean }> {
    const flags = (await this.document?.getPermissions()) ?? null;
    if (flags === null) return { download: true, print: true };
    return {
      download: flags.includes(PermissionFlag.COPY),
      print: flags.includes(PermissionFlag.PRINT),
    };
  }

  /** ページ寸法（pt）はマニフェストを正とする — サーバ生成の 1 つの真実。 */
  private pageSize(pageNo: number): { width: number; height: number } {
    const info = this.session?.current.pages[pageNo - 1];
    if (!info) {
      throw new ViewerError('E_STATE', `ページ ${pageNo} はマニフェストに存在しない`);
    }
    return { width: info.width, height: info.height };
  }

  maxRasterWidth(pageNo: number): number {
    const { width, height } = this.pageSize(pageNo);
    return maxWidthFor(height / width);
  }

  /**
   * PDF はベクタなので原理的な解像度上限は無い。暴走を防ぐための安全弁だけを置く
   * （領域を切れば実効解像度はここまで上げられる — canvas 上限とは別物）。
   */
  maxEffectiveWidth(): number {
    return MAX_EFFECTIVE_WIDTH;
  }

  private page(pageNo: number): Promise<PDFPageProxy> {
    const cached = this.pages.get(pageNo);
    if (cached) return cached;
    const document = this.document;
    if (!document) {
      return Promise.reject(new ViewerError('E_STATE', 'ソースが未オープン'));
    }
    const pending = document.getPage(pageNo);
    this.pages.set(pageNo, pending);
    return pending;
  }

  /**
   * ページ（または領域）を描画する。
   *
   * 領域指定時は canvas を領域ぶんだけ作り、transform でページ内オフセットを打ち消す
   * （＝部分再描画）。これにより canvas 上限に縛られずに実効解像度を上げられる。
   */
  async renderPage(request: PageRasterRequest): Promise<PageRaster> {
    const { pageNo, targetWidth, signal } = request;
    const region = request.region ?? FULL_PAGE;
    const page = await this.page(pageNo);
    signal?.throwIfAborted();

    // 領域の目標幅 → ページ全面換算の幅 → pdf.js の倍率
    const fullWidth = Math.max(1, Math.round(targetWidth / region.w));
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: fullWidth / base.width });

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(region.w * viewport.width));
    canvas.height = Math.max(1, Math.round(region.h * viewport.height));
    const context = canvas.getContext('2d');
    if (!context) {
      throw new ViewerError('E_CANVAS', '2D コンテキストを取得できない');
    }

    const task = page.render({
      canvas,
      canvasContext: context,
      viewport,
      // 領域の左上が canvas の原点に来るようにページをずらして描く
      transform: [1, 0, 0, 1, -region.x * viewport.width, -region.y * viewport.height],
    });
    const cancel = () => task.cancel();
    signal?.addEventListener('abort', cancel, { once: true });
    try {
      await task.promise;
    } finally {
      signal?.removeEventListener('abort', cancel);
    }
    signal?.throwIfAborted();

    return {
      pageNo,
      bitmap: canvas,
      width: canvas.width,
      height: canvas.height,
      region,
      close: () => {
        // canvas はサイズ 0 にすると保持していたピクセルバッファが解放される。
        canvas.width = 0;
        canvas.height = 0;
      },
    };
  }

  async dispose(): Promise<void> {
    this.pages.clear();
    // destroy() は worker の終了まで待つ（dispose 後の worker 残留ゼロ・M2 verify）。
    await this.loadingTask?.destroy();
    this.loadingTask = null;
    this.document = null;
    this.transport?.abort();
    this.transport = null;
    this.session = null;
  }
}
